import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ExpandableText from "../../components/ExpandableText";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import {
  LogOut, CheckCircle2, ArrowRight, Trophy, FileDown, Award, FileText, Home,
  Receipt, BookOpen, Route, KeyRound, ChevronDown, UserCircle2, MessageCircle, Lock,
} from "lucide-react";
import { toast } from "sonner";
import LearningPathModal from "../../components/LearningPathModal";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const examActionLabel = (ex) => {
  if (ex.attempts === 0) return "Empezar examen";
  if (ex.status === "passed") return "Repetir examen";
  return "Reintentar";
};

export default function UserDashboard() {
  useDocumentTitle("Mis cursos");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pathOpen, setPathOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/me/assignments")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const downloadCertificate = async (courseId, title, isFinal) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/me/courses/${courseId}/diploma`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || (isFinal ? "No se pudo descargar el diploma" : "No se pudo descargar el certificado"));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isFinal ? "diploma" : "certificado"}-${(title || "curso").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadConsolidated = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/me/consolidated`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar el diploma consolidado");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `diploma-consolidado-${(user?.name || "alumno").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const total = items.length;
  const passed = items.filter((i) => i.all_exams_passed).length;
  const overall = total === 0 ? 0 : Math.round((passed / total) * 100);
  const passedItems = items.filter((i) => i.all_exams_passed);
  const totalHours = passedItems.reduce((acc, i) => acc + (Number(i.study_hours) || 0), 0);
  const finalsPassed = passedItems.filter((i) => i.kind === "final").length;
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="user-dashboard">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.jpg" alt="laclaseencasa" className="w-8 h-8 object-contain flex-shrink-0" />
            <span className="font-display text-lg text-brand-ink truncate">laclaseencasa</span>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <Link to="/dashboard/orders" data-testid="user-my-orders" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                <Receipt className="w-4 h-4 mr-1" />
                <span className="hidden lg:inline">Mis pedidos</span>
                <span className="lg:hidden">Pedidos</span>
              </Button>
            </Link>
            <Link to="/" data-testid="user-back-to-home" className="hidden md:block">
              <Button variant="ghost" size="sm">
                <Home className="w-4 h-4 mr-1" />
                <span className="hidden lg:inline">Volver a la home</span>
                <span className="lg:hidden">Home</span>
              </Button>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="max-w-[220px]"
                  data-testid="user-account-menu-trigger"
                >
                  <UserCircle2 className="w-4 h-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{firstName || "Cuenta"}</span>
                  <ChevronDown className="w-3.5 h-3.5 ml-1 flex-shrink-0 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="user-account-menu">
                <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
                  <p className="truncate text-brand-ink font-medium text-sm">{user?.name || "Alumno"}</p>
                  {user?.email && <p className="truncate text-[11px] text-gray-500 mt-0.5">{user.email}</p>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="sm:hidden">
                  <Link to="/dashboard/orders" data-testid="user-menu-orders">
                    <Receipt className="w-4 h-4 mr-2" /> Mis pedidos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="md:hidden">
                  <Link to="/" data-testid="user-menu-home">
                    <Home className="w-4 h-4 mr-2" /> Volver a la home
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/help" data-testid="user-menu-manual">
                    <BookOpen className="w-4 h-4 mr-2" /> Manual
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setPathOpen(true)}
                  data-testid="user-menu-learning-path"
                >
                  <Route className="w-4 h-4 mr-2" /> Mi trayecto formativo
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setChangePwOpen(true)}
                  data-testid="user-menu-change-password"
                >
                  <KeyRound className="w-4 h-4 mr-2" /> Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl text-gray-900">
              Hola {user?.first_name || user?.name?.split(" ")[0] || user?.name}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Estos son los cursos que tienes asignados. Aprueba los exámenes en orden hasta completarlos todos.
            </p>
            <a
              href="https://wa.me/34622669920"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm text-[#128C7E] hover:text-[#075E54] transition-colors"
              data-testid="user-whatsapp-link"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp de formación: <strong>622 669 920</strong></span>
            </a>
          </div>
          <Card className="p-5 border border-gray-200 shadow-none rounded-lg w-full md:w-72">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Progreso global</span>
              <Trophy className="w-4 h-4 text-amber-500" />
            </div>
            <p className="font-display text-3xl text-gray-900">
              {passed}<span className="text-gray-400 text-xl">/{total}</span>
            </p>
            <Progress value={overall} className="mt-3 h-2" />
          </Card>
        </div>

        {loading ? (
          <p className="text-gray-500">Cargando…</p>
        ) : items.length === 0 ? (
          <Card className="p-8 border border-gray-200 shadow-none rounded-lg text-center">
            <img
              src="/logo.jpg"
              alt=""
              className="w-32 h-32 object-contain mx-auto opacity-60"
            />
            <p className="mt-4 text-gray-600">
              Aún no tienes cursos asignados. Vuelve más tarde.
            </p>
          </Card>
        ) : (
          <>
            {passedItems.length > 0 && !user?.is_apl && !user?.is_tk && (
              <Card
                className="mb-8 p-6 border border-[#e7d5b4] bg-gradient-to-br from-[#FBF8F2] to-white shadow-none rounded-lg"
                data-testid="consolidated-card"
              >
                <div className="flex items-start gap-5 flex-wrap">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#fdf2d9] border border-[#e7d5b4] flex items-center justify-center">
                    <Award className="w-7 h-7 text-[#8a6f43]" />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#8a6f43] font-mono-data mb-1">
                      Tu acreditación profesional
                    </p>
                    <h2 className="font-display text-2xl text-brand-ink">
                      Diploma consolidado
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Un solo documento con todos los cursos que has completado y el total de horas lectivas acumuladas.
                    </p>
                    <div className="flex gap-5 mt-4 text-sm flex-wrap">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Cursos</p>
                        <p className="font-display text-2xl text-brand-ink">{passedItems.length}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Exámenes finales</p>
                        <p className="font-display text-2xl text-[#8a6f43]">{finalsPassed}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Horas lectivas</p>
                        <p className="font-display text-2xl text-brand">
                          {Number.isInteger(totalHours) ? totalHours : totalHours.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="bg-[#8a6f43] hover:bg-[#6f5934] text-white"
                    onClick={downloadConsolidated}
                    data-testid="download-consolidated"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Descargar consolidado
                  </Button>
                </div>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((it, i) => {
              const sortedExams = [...(it.exams || [])].sort((a, b) => a.order - b.order);
              return (
                <Card
                  key={it.assignment_id}
                  className="p-6 border border-gray-200 shadow-none rounded-lg hover:border-gray-300 transition-all fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`assignment-${it.title}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-xl text-gray-900">{it.title}</h3>
                    {it.all_exams_passed && (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs bg-emerald-50 px-2 py-1 rounded-full flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aprobado
                      </span>
                    )}
                  </div>
                  {it.price != null && it.price >= 0 && (
                    <div className="mb-3">
                      <span
                        className="inline-flex items-center font-mono-data text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                        data-testid={`price-${it.title}`}
                      >
                        {it.price === 0
                          ? "Gratis"
                          : new Intl.NumberFormat("es-ES", {
                              style: "currency",
                              currency: "EUR",
                              minimumFractionDigits: Number.isInteger(it.price) ? 0 : 2,
                            }).format(it.price)}
                      </span>
                    </div>
                  )}
                  {it.description && (
                    <div className="mb-3">
                      <ExpandableText
                        text={it.description}
                        className="text-sm text-gray-600"
                        testId={`description-${it.course_id}`}
                      />
                    </div>
                  )}

                  {it.general_theory_file && (
                    <a
                      href={`/reader/course/${it.course_id}/${it.general_theory_file.file_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-3 text-sm text-brand hover:text-brand-hover hover:underline flex items-center gap-1.5"
                      data-testid={`general-theory-${it.title}`}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{it.general_theory_file.original_name}</span>
                      <span className="text-[10px] text-gray-400">(material general, solo lectura)</span>
                    </a>
                  )}

                  <div className="space-y-2 my-3">
                    {sortedExams.map((ex) => (
                      <div
                        key={ex.exam_id}
                        className={`border rounded-md p-3 ${ex.status === "locked" ? "bg-gray-50 border-gray-100" : "border-gray-200"}`}
                        data-testid={`exam-row-${ex.exam_id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${ex.status === "locked" ? "text-gray-400" : "text-gray-900"}`}>
                              {ex.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {ex.status === "passed"
                                ? `Aprobado · ${ex.passed_score ?? ex.best_score ?? "—"}%`
                                : ex.attempts > 0
                                ? `Mejor puntuación: ${ex.best_score ?? "—"}% · Objetivo: ${ex.pass_percentage}%`
                                : ex.status === "locked"
                                ? "Bloqueado hasta aprobar el examen anterior"
                                : `Objetivo: ${ex.pass_percentage}%`}
                              {ex.time_limit_minutes > 0 ? ` · ⏱ ${ex.time_limit_minutes} min` : ""}
                            </p>
                            {ex.status !== "passed" && ex.theory_files?.length > 0 && (
                              <ul className="mt-1.5 space-y-1">
                                {ex.theory_files.map((f) => (
                                  <li key={f.file_id}>
                                    <a
                                      href={`/reader/exam/${it.course_id}/${ex.exam_id}/${f.file_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-brand hover:text-brand-hover hover:underline flex items-center gap-1"
                                      data-testid={`open-theory-${f.file_id}`}
                                    >
                                      <FileDown className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{f.original_name}</span>
                                      <span className="text-[10px] text-gray-400">(solo lectura)</span>
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {ex.status === "locked" ? (
                            <Button size="sm" variant="outline" disabled className="flex-shrink-0" data-testid={`locked-${ex.exam_id}`}>
                              <Lock className="w-3.5 h-3.5 mr-1.5" /> Bloqueado
                            </Button>
                          ) : (
                            <Link to={`/exam/${it.course_id}/${ex.exam_id}`} className="flex-shrink-0">
                              <Button
                                size="sm"
                                className={ex.status === "passed" ? "" : "bg-brand hover:bg-brand-hover text-white"}
                                variant={ex.status === "passed" ? "outline" : "default"}
                                data-testid={`start-exam-${ex.exam_id}`}
                              >
                                {examActionLabel(ex)}
                                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {it.diploma_ready && !user?.is_apl && !user?.is_tk && (
                    <Button
                      variant="outline"
                      className="w-full mt-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      onClick={() => downloadCertificate(it.course_id, it.title, it.kind === "final")}
                      data-testid={`download-cert-${it.title}`}
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      {it.kind === "final" ? "Descargar diploma" : "Descargar certificado"}
                    </Button>
                  )}
                  {it.all_exams_passed && !it.diploma_ready && !user?.is_apl && !user?.is_tk && (
                    <p
                      className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2 text-center"
                      data-testid={`installment-pending-${it.title}`}
                    >
                      Todos los exámenes aprobados. El diploma/certificado estará disponible cuando se complete el pago aplazado.
                    </p>
                  )}
                  {it.all_exams_passed && (user?.is_apl || user?.is_tk) && (
                    <p
                      className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2 text-center"
                      data-testid={`apl-manual-notice-${it.title}`}
                    >
                      El diploma/certificado será emitido manualmente por el centro.
                    </p>
                  )}
                </Card>
              );
            })}
            </div>
          </>
        )}
      </main>
      <LearningPathModal open={pathOpen} onOpenChange={setPathOpen} variant="self" />
      <ChangePasswordModal open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
